// src/components/practice/tasks/MatchingSituations.jsx
import React from 'react';
import { marked } from 'marked';
import MatchingQuestion from '../MatchingQuestion';
import { X } from 'lucide-react';

/**
 * FINAL VERSION - Paper Clipping Style with Rotation Variety
 * Different angles for each ad, fully dynamic from JSON, no blur effects
 */
const MatchingSituations = ({
  partData,
  questions,
  examples,
  partAnswers,
  handleBatchAnswerChange,
  flaggedQuestions,
  toggleFlagQuestion,
  isMobileAnswerSheetOpen,
  onCloseMobileAnswerSheet
}) => {
  // This list contains the REAL situations
  const situations = (partData.content.situations || [])
    .map(s => ({
      questionId: questions.find(q => q.questionNumber.toString() === s.id)?.questionId,
      number: s.id,
      text: s.text,
    }))
    .filter(s => s.questionId);

  const advertisements = partData.content.advertisements || partData.content.texts || [];

  const realAnswers = Object.values(partAnswers);
  // ÖSD: the ad of an example may be used again, so examples lock nothing.
  const exampleAnswers = partData.examProvider === 'OSD' ? [] : examples.map(ex => ex.correctAnswer);
  const usedAdIds = new Set([...realAnswers, ...exampleAnswers]);

  // Different rotation angles for variety (no blur)
  const rotationStyles = [
    { transform: 'rotate(-0.5deg)' },
    { transform: 'rotate(0deg)' },
    { transform: 'rotate(0.5deg)' },
    { transform: 'rotate(-0.3deg)' },
    { transform: 'rotate(0deg)' },
    { transform: 'rotate(0.4deg)' },
    { transform: 'rotate(-0.6deg)' },
    { transform: 'rotate(0.3deg)' }
  ];

  // Assign rotation style to each ad
  const getStyleForAd = (index) => {
    return rotationStyles[index % rotationStyles.length];
  };

  // --- LEFT COLUMN CONTENT (Reading) ---
  const leftContent = (
    <div className="space-y-6">
      <div className="bg-white p-6 shadow-sm border border-gray-200">
        <h3 className="text-xl font-bold text-gray-900 border-b-2 border-gray-300 pb-3 mb-6">
          Anzeigen
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {advertisements.map((ad, index) => {
            const isUsed = usedAdIds.has(ad.id);
            const rotationStyle = getStyleForAd(index);

            return (
              <div
                key={ad.id}
                className="relative"
                style={{ paddingTop: '20px', paddingLeft: '20px' }}
              >
                {/* Letter Badge in Top-Left Corner */}
                <div className="absolute top-0 left-0 w-10 h-10 bg-white border-2 border-gray-800 flex items-center justify-center shadow-md z-10">
                  <span className={`font-bold text-lg ${isUsed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                    {ad.id.toUpperCase()}
                  </span>
                </div>

                {/* Paper Clipping Card with Rotation */}
                <div
                  className={`
                    p-5 bg-white 
                    border-2 border-gray-800
                    shadow-lg
                    relative
                    transition-all duration-300
                    ${isUsed ? 'opacity-50' : 'opacity-100'}
                  `}
                  style={{
                    minHeight: '200px',
                    paddingTop: '25px',
                    ...rotationStyle
                  }}
                >
                  {/* Advertisement Content - Dynamic from JSON */}
                  <div
                    className="prose prose-sm max-w-none text-gray-900"
                    style={{
                      fontSize: '13px',
                      lineHeight: '1.5'
                    }}
                    dangerouslySetInnerHTML={{ __html: marked.parse(ad.text || ad.content) }}
                  />

                  {/* Strikethrough overlay if used */}
                  {isUsed && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-full h-0.5 bg-gray-400 transform -rotate-12"></div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // --- RIGHT COLUMN CONTENT (Answering) ---
  const rightContent = (
    <div>
      {/* Answer Sheet Header */}
      <div className="border-2 border-gray-900 bg-white p-3 rounded-none mb-4">
        <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide">
          Antwortbogen
        </h3>
        <p className="text-xs text-gray-600 mt-1">
          Weisen Sie jeder Situation eine Anzeige zu.
        </p>
      </div>

      <div className="border-2 border-gray-900 bg-white rounded-none">

        {/* Examples - Dynamic from JSON */}
        {examples.map((ex, idx) => {
          const correctAd = advertisements.find(ad => ad.id === ex.correctAnswer);
          const correctAdText = correctAd ? `${correctAd.id.toUpperCase()}` : ex.correctAnswer;
          const situationText = ex.situationText || ex.questionText || ex.statement || ex.question || "";

          return (
            <div
              key={`ex-${idx}`}
              className="bg-yellow-50 p-4 opacity-90 border-b border-gray-300"
            >
              <div className="flex items-center justify-between">
                {/* Left side: Example Number and Text */}
                <div className="flex-1 flex items-start">
                  <span className="font-bold text-sm mr-2 pt-0.5">
                    {ex.questionNumber || '0'}.
                  </span>
                  <span className="text-sm leading-relaxed">
                    {situationText}
                  </span>
                </div>

                {/* Right side: Disabled "Answer" */}
                <div className="ml-4 flex-shrink-0">
                  <div
                    className="border-2 border-gray-900 bg-gray-900 text-white font-bold text-sm px-3 py-1.5 rounded-none cursor-not-allowed"
                    style={{ minWidth: '70px', textAlign: 'center' }}
                  >
                    {correctAdText}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Regular Questions - Dynamic from JSON */}
        <div className="p-4">
          <MatchingQuestion
            questions={situations}
            options={[...advertisements, { id: partData.examProvider === 'TELC' ? 'x' : '0' }]}
            savedAnswers={partAnswers}
            onAnswerChange={handleBatchAnswerChange}
            title="Anzeige wählen"
            flaggedQuestions={flaggedQuestions}
            toggleFlagQuestion={toggleFlagQuestion}
            exampleAnswers={exampleAnswers}
          />
        </div>
      </div>
    </div>
  );

  const mobileOverlay = (isMobileAnswerSheetOpen && onCloseMobileAnswerSheet) ? (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end md:hidden" aria-modal="true" role="dialog" aria-label="Antwortbogen">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCloseMobileAnswerSheet} aria-hidden />
      <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[90vh] flex flex-col animate-slide-up">
        <div className="flex items-center justify-between flex-shrink-0 p-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-900">Antwortbogen</h3>
          <button type="button" onClick={onCloseMobileAnswerSheet} className="p-2 -m-2 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation min-w-[48px] min-h-[48px] flex items-center justify-center" aria-label="Schließen">
            <X className="w-6 h-6 text-gray-700" />
          </button>
        </div>
        <div className="overflow-y-auto overscroll-contain p-4 pb-safe">{rightContent}</div>
      </div>
    </div>
  ) : null;

  return (
    <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-6">
      <div className="grid grid-cols-1 lg:grid-cols-5 lg:gap-8">
        <div className="lg:col-span-3 min-w-0">
          {leftContent}
        </div>
        <div className="hidden lg:block lg:col-span-2 mt-6 lg:mt-0">
          <div className="lg:sticky lg:top-6">
            {rightContent}
          </div>
        </div>
      </div>
      {mobileOverlay}
    </div>
  );
};

export default MatchingSituations;